import { type ReactNode, useEffect, useRef, useState } from "react";

import { VinylSpinner } from "@/components/vinyl-spinner";

interface FallbackImageAssetProps {
  alt: string;
  className?: string;
  fallback: ReactNode;
  objectFit: "contain" | "cover";
  onError?: () => void;
  onLoad?: () => void;
  priority: boolean;
  showLoadingSpinner: boolean;
  sizes?: string;
  src: string;
  srcSet?: string;
}

export function FallbackImageAsset({
  alt,
  className,
  fallback,
  objectFit,
  onError,
  onLoad,
  priority,
  showLoadingSpinner,
  sizes,
  src,
  srcSet,
}: FallbackImageAssetProps) {
  const [status, setStatus] = useState<"failed" | "loaded" | "loading">("loading");
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const image = imgRef.current;
    if (status !== "loading" || !image?.complete || image.naturalWidth === 0) return;

    setStatus("loaded");
    onLoad?.();
  }, [onLoad, status]);

  if (status === "failed") return fallback;

  return (
    <>
      {showLoadingSpinner && status === "loading" && (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
          aria-hidden="true"
        >
          <VinylSpinner className="w-1/2 h-1/2" />
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        srcSet={srcSet}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        sizes={sizes}
        className={className}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit,
        }}
        onLoad={() => {
          setStatus("loaded");
          onLoad?.();
        }}
        onError={() => {
          setStatus("failed");
          onError?.();
        }}
      />
    </>
  );
}
